import { useState, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { theme } from '../../styles/theme';
import { Logo } from '../../components/Logo';
import { Seo } from '../../lib/seo';

import { AdminDashboard } from './tabs/Dashboard';
import { AdminProductos } from './tabs/Productos';
import { AdminInventario } from './tabs/Inventario';
import { AdminCompras } from './tabs/Compras';
import { AdminClientes } from './tabs/Clientes';
import { AdminPedidos } from './tabs/Pedidos';
import { AdminCotizaciones } from './tabs/Cotizaciones';
import { AdminFacturacion } from './tabs/Facturacion';
import { AdminPagos } from './tabs/Pagos';
import { AdminEntregas } from './tabs/Entregas';
import { AdminReportes } from './tabs/Reportes';
import { AdminMarketingLutron } from './tabs/MarketingLutron';
import { AdminAIContent } from './tabs/AIContent';
import { AdminImportarLutron } from './tabs/ImportarLutron';
import { AdminFotos } from './tabs/Fotos';
import { AdminFichasCatalogos } from './tabs/FichasCatalogos';
import { AdminWhatsApp } from './tabs/WhatsApp';
import { AdminNurture } from './tabs/Nurture';
import { AdminMarketing } from './tabs/Marketing';
import { AdminUsuarios } from './tabs/Usuarios';
import { AdminFinanzas } from './tabs/Finanzas';
import { AdminCRM } from './tabs/CRM';

export type AdminTab =
  | 'dashboard' | 'productos' | 'fotos' | 'inventario' | 'compras'
  | 'crm' | 'clientes' | 'pedidos' | 'cotizaciones' | 'facturacion' | 'pagos'
  | 'entregas' | 'reportes' | 'marketing_lutron' | 'ai_content' | 'importar_lutron' | 'whatsapp' | 'nurture' | 'marketing' | 'fichas_catalogos' | 'finanzas' | 'usuarios';

export function AdminShell() {
  const { staff, signOut } = useAuth();
  const [tab, setTab] = useState<AdminTab>('dashboard');
  const navigate = useNavigate();

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: theme.bgSoft,
      color: theme.text,
      fontFamily: theme.font.sans,
    }}>
      <Seo title="Admin · NULED OS" description="Backoffice NULED" />

      {/* Sidebar */}
      <aside style={{
        width: 240,
        background: theme.brand,
        color: theme.textOnBrand,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}>
        <div style={{ padding: '24px 20px 12px' }}>
          <Link to="/" style={{ color: theme.textOnBrand, display: 'flex' }}>
            <Logo height={28} />
          </Link>
          <div style={{
            fontSize: 10,
            color: theme.accent,
            marginTop: 8,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            fontWeight: 700,
          }}>
            OS · {staff?.role ?? 'staff'}
          </div>
        </div>

        <div style={{ padding: '0 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
            {staff?.display_name}
          </div>
        </div>

        <nav style={{ padding: 8, flex: 1, overflowY: 'auto' }}>
          <NavItem icon="📊" label="Dashboard"     active={tab === 'dashboard'}    onClick={() => setTab('dashboard')} />

          <SectionLabel>Catálogo</SectionLabel>
          <NavItem icon="📦" label="Productos"      active={tab === 'productos'}    onClick={() => setTab('productos')} />
          <NavItem icon="🖼️" label="Fotos"          active={tab === 'fotos'}        onClick={() => setTab('fotos')} />
          <NavItem icon="📥" label="Importar Lutron" active={tab === 'importar_lutron'} onClick={() => setTab('importar_lutron')} />
          <NavItem icon="📋" label="Inventario"      active={tab === 'inventario'}   onClick={() => setTab('inventario')} />
          <NavItem icon="🚚" label="Compras"         active={tab === 'compras'}      onClick={() => setTab('compras')} />
          <NavItem icon="📄" label="Fichas y Catálogos" active={tab === 'fichas_catalogos'} onClick={() => setTab('fichas_catalogos')} />

          <SectionLabel>Comercial</SectionLabel>
          <NavItem icon="🎯" label="CRM"             active={tab === 'crm'}          onClick={() => setTab('crm')} />
          <NavItem icon="👥" label="Clientes"        active={tab === 'clientes'}     onClick={() => setTab('clientes')} />
          <NavItem icon="📝" label="Cotizaciones"    active={tab === 'cotizaciones'} onClick={() => setTab('cotizaciones')} />
          <NavItem icon="🧾" label="Pedidos"         active={tab === 'pedidos'}      onClick={() => setTab('pedidos')} />
          <NavItem icon="📄" label="Facturación"     active={tab === 'facturacion'}  onClick={() => setTab('facturacion')} />
          <NavItem icon="💰" label="Pagos"            active={tab === 'pagos'}        onClick={() => setTab('pagos')} />
          <NavItem icon="📮" label="Entregas"         active={tab === 'entregas'}     onClick={() => setTab('entregas')} />

          <SectionLabel>Marketing</SectionLabel>
          <NavItem icon="🚀" label="Marketing IA"     active={tab === 'marketing'}         onClick={() => setTab('marketing')} />
          <NavItem icon="💬" label="WhatsApp"         active={tab === 'whatsapp'}          onClick={() => setTab('whatsapp')} />
          <NavItem icon="📨" label="Nurture leads"    active={tab === 'nurture'}           onClick={() => setTab('nurture')} />
          <NavItem icon="🎨" label="Fotos Lutron"     active={tab === 'marketing_lutron'} onClick={() => setTab('marketing_lutron')} />
          <NavItem icon="🤖" label="Contenido AI"      active={tab === 'ai_content'}       onClick={() => setTab('ai_content')} />

          <SectionLabel>Análisis</SectionLabel>
          <NavItem icon="📈" label="Reportes"         active={tab === 'reportes'}     onClick={() => setTab('reportes')} />

          {staff?.role === 'admin' && (
            <>
              <SectionLabel>Administración</SectionLabel>
              <NavItem icon="💵" label="Finanzas"      active={tab === 'finanzas'}     onClick={() => setTab('finanzas')} />
              <NavItem icon="👤" label="Usuarios"      active={tab === 'usuarios'}     onClick={() => setTab('usuarios')} />
            </>
          )}
        </nav>

        <div style={{ padding: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Link to="/" style={{
            display: 'block',
            color: 'rgba(255,255,255,0.55)',
            fontSize: 12,
            textDecoration: 'none',
            marginBottom: 8,
          }}>
            ← Sitio público
          </Link>
          <button
            onClick={async () => { await signOut(); navigate('/'); }}
            style={{
              width: '100%',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.3)',
              color: theme.textOnBrand,
              padding: '8px 12px',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              cursor: 'pointer',
              fontFamily: theme.font.sans,
            }}
          >
            Cerrar sesión
          </button>
        </div>
      </aside>

      <main style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
        {tab === 'dashboard'    && <AdminDashboard onTab={setTab} />}
        {tab === 'productos'    && <AdminProductos />}
        {tab === 'fotos'        && <AdminFotos />}
        {tab === 'inventario'   && <AdminInventario />}
        {tab === 'compras'      && <AdminCompras />}
        {tab === 'crm'          && <AdminCRM />}
        {tab === 'clientes'     && <AdminClientes />}
        {tab === 'pedidos'      && <AdminPedidos />}
        {tab === 'facturacion'  && <AdminFacturacion />}
        {tab === 'cotizaciones' && <AdminCotizaciones />}
        {tab === 'pagos'        && <AdminPagos />}
        {tab === 'entregas'     && <AdminEntregas />}
        {tab === 'reportes'     && <AdminReportes />}
        {tab === 'marketing'     && <AdminMarketing />}
        {tab === 'whatsapp'      && <AdminWhatsApp />}
        {tab === 'nurture'       && <AdminNurture />}
        {tab === 'marketing_lutron' && <AdminMarketingLutron />}
        {tab === 'ai_content'    && <AdminAIContent />}
        {tab === 'importar_lutron' && <AdminImportarLutron />}
        {tab === 'fichas_catalogos' && <AdminFichasCatalogos />}
        {tab === 'finanzas'      && <AdminFinanzas />}
        {tab === 'usuarios'      && <AdminUsuarios />}
      </main>
    </div>
  );
}

function NavItem({ icon, label, active, onClick }: { icon: string; label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '9px 12px',
      background: active ? theme.accent : 'transparent',
      color: active ? theme.brand : theme.textOnBrand,
      border: 'none',
      cursor: 'pointer',
      fontFamily: theme.font.sans,
      fontSize: 12.5,
      fontWeight: active ? 700 : 500,
      textAlign: 'left',
      borderRadius: 0,
      marginBottom: 1,
    }}>
      <span style={{ fontSize: 13, opacity: 0.85 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      padding: '14px 12px 6px',
      fontSize: 9,
      color: theme.accent,
      fontWeight: 700,
      letterSpacing: 1.5,
      textTransform: 'uppercase',
    }}>
      {children}
    </div>
  );
}

// === Helpers compartidos por tabs ===

export function AdminContainer({ title, eyebrow, action, children }: {
  title: string; eyebrow: string; action?: ReactNode; children: ReactNode;
}) {
  return (
    <div style={{ padding: '32px 32px 64px', maxWidth: 1400 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{
            fontSize: 11,
            color: theme.accentDark,
            textTransform: 'uppercase',
            letterSpacing: 2,
            fontWeight: 700,
          }}>
            {eyebrow}
          </div>
          <h1 style={{
            fontFamily: theme.font.head,
            fontSize: 30,
            fontWeight: 400,
            color: theme.brand,
            margin: '4px 0 0',
          }}>
            {title}
          </h1>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function AdminComingSoon({ what, suggestion }: { what: string; suggestion?: string }) {
  return (
    <div style={{
      padding: 40,
      background: theme.bg,
      border: `1px dashed ${theme.border}`,
      textAlign: 'center',
      color: theme.textMuted,
    }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🚧</div>
      <div style={{ fontFamily: theme.font.head, fontSize: 18, color: theme.brand, fontWeight: 500, marginBottom: 4 }}>
        {what} · construyendo
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 420, margin: '0 auto' }}>
        {suggestion ?? 'Esta sección llega en los próximos commits. Iteramos en producción.'}
      </div>
    </div>
  );
}
